/**
 * Shared week/month date-range helpers for the kanban "period filter"
 * used across the client, admin, and maid-portal job boards.
 */

export const startOfWeek = (date) => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Monday-based week
  d.setDate(diff);
  d.setHours(0, 0, 0, 0);
  return d;
};

export const endOfWeek = (date) => {
  const s = startOfWeek(date);
  const e = new Date(s);
  e.setDate(s.getDate() + 6);
  e.setHours(23, 59, 59, 999);
  return e;
};

export const startOfMonth = (date) => {
  const d = new Date(date.getFullYear(), date.getMonth(), 1);
  d.setHours(0, 0, 0, 0);
  return d;
};

export const endOfMonth = (date) => {
  const d = new Date(date.getFullYear(), date.getMonth() + 1, 0);
  d.setHours(23, 59, 59, 999);
  return d;
};

/** Returns { start, end } Date objects for the given period type + anchor date. */
export const getPeriodRange = (periodType, anchor) =>
  periodType === 'week'
    ? { start: startOfWeek(anchor), end: endOfWeek(anchor) }
    : { start: startOfMonth(anchor), end: endOfMonth(anchor) };

/** Human-readable label, e.g. "Jun 16 – Jun 22" (week) or "June 2026" (month). */
export const getPeriodLabel = (periodType, start, end, anchor) =>
  periodType === 'week'
    ? `${start.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} – ${end.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`
    : anchor.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });

/** True when the selected period is the one containing "now". */
export const isCurrentPeriod = (periodType, start, anchor) => {
  const now = new Date();
  return periodType === 'week'
    ? startOfWeek(now).toDateString() === start.toDateString()
    : now.getMonth() === anchor.getMonth() && now.getFullYear() === anchor.getFullYear();
};

/** Returns a new anchor shifted by `delta` periods (weeks or months). */
export const shiftAnchor = (anchor, periodType, delta) => {
  const next = new Date(anchor);
  if (periodType === 'week') {
    next.setDate(anchor.getDate() + delta * 7);
  } else {
    next.setMonth(anchor.getMonth() + delta);
  }
  return next;
};

/** True when `dateLike` falls within [start, end]. Safe against bad dates. */
export const isWithinRange = (dateLike, start, end) => {
  if (!dateLike) return false;
  const t = new Date(dateLike).getTime();
  if (Number.isNaN(t)) return false;
  return t >= start.getTime() && t <= end.getTime();
};
